import { useCallback, useEffect } from "react"
import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from "@mui/material"
import { forEach } from "lodash"
import type { DeepKeys } from "@tanstack/react-form"
import { SETUP_GOALS, SetupGoal } from "connected-components/optimize-experiments/constants"
import { useFormContext } from "connected-components/optimize-experiments/form-context"
import {
  computeGoalValue,
  GoalRadio,
  type GoalValue,
} from "connected-components/optimize-experiments/components"
import Forecast, { type ForecastMeta } from "connected-components/optimize-experiments/components/forecast"
import type { FormSections } from "connected-components/optimize-experiments/configuration-sections"
import { targetTotalRiskSchema } from "connected-components/optimize-experiments/form-validation-schemas/goals"
import { forecastValidator } from "connected-components/optimize-experiments/form-validation-schemas/forecast"
import type { DataStatus } from "helpers/data-statuses"

import "./style.scss"

interface SetupProps {
  currentTotalRiskValue: number | undefined
  totalRiskStatus: string
  forecasts: ForecastMeta[]
  forecastsStatus: DataStatus
  hasBase: boolean
  disableForecasts: boolean
}

interface GoalConfig {
  fieldName?: DeepKeys<FormSections>
  absoluteLabel: string
  activeLabel: string
  valueUnit: string
}

const GOAL_SELECTION_OPTIONS: Record<SetupGoal, GoalConfig> = {
  [SETUP_GOALS.TARGET_TOTAL_RISK]: {
    fieldName: "setup.targetTotalRiskValue",
    absoluteLabel: "Target Total Risk",
    activeLabel: "Target Active Risk",
    valueUnit: "%",
  },
  [SETUP_GOALS.MIN_VOLATILITY]: {
    absoluteLabel: "Min Volatility",
    activeLabel: "Minimize Tracking Error",
    valueUnit: "%",
  },
  [SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK]: {
    absoluteLabel: "Maximize Specific Risk",
    activeLabel: "Maximize Active Idio",
    valueUnit: "%",
  },
}

const getGoalLabel = (config: GoalConfig, hasBase: boolean): string =>
  hasBase ? config.activeLabel : config.absoluteLabel

export default function Setup({
  currentTotalRiskValue,
  totalRiskStatus,
  forecasts,
  forecastsStatus,
  hasBase,
  disableForecasts,
}: SetupProps) {
  const form = useFormContext()
  const currentTotalRiskGoalValue: GoalValue = computeGoalValue(currentTotalRiskValue, totalRiskStatus)

  const clearAllGoalValues = useCallback(() => {
    forEach(GOAL_SELECTION_OPTIONS, (option: GoalConfig) => {
      if (option.fieldName) {
        form.setFieldValue(option.fieldName, undefined)
      }
    })
  }, [form])

  const handleGoalChange = useCallback((goal: SetupGoal) => {
    clearAllGoalValues()
    if (GOAL_SELECTION_OPTIONS[goal].fieldName) {
      form.setFieldValue(GOAL_SELECTION_OPTIONS[goal].fieldName, currentTotalRiskGoalValue.inputValue)
    }
  }, [clearAllGoalValues, form, currentTotalRiskGoalValue.inputValue])

  useEffect(() => {
    const currentGoal = form.state.values.setup.goal
    if (disableForecasts && currentGoal === SETUP_GOALS.TARGET_TOTAL_RISK) {
      form.setFieldValue("setup.goal", SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK)
      handleGoalChange(SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK)
    }
  }, [disableForecasts, form, handleGoalChange])

  const totalTargetRiskConfig = GOAL_SELECTION_OPTIONS[SETUP_GOALS.TARGET_TOTAL_RISK]
  const minVolatilityConfig = GOAL_SELECTION_OPTIONS[SETUP_GOALS.MIN_VOLATILITY]
  const maxSpecificRiskConfig = GOAL_SELECTION_OPTIONS[SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK]

  return (
    <Box className="components_setup">
      <Typography color="inherit" noWrap className="setup-label">
        Setup
      </Typography>
      <Typography color="secondary" variant="caption">
        Choose a goal to define the target portfolio.
      </Typography>
      <Typography color="inherit" noWrap className="setup-section-label">
        {hasBase ? "Select Active Goal" : "Select Absolute Goal"}
      </Typography>
      <FormControl component="fieldset" fullWidth>
        <form.Field name="setup.goal">
          {(field) => (
            <RadioGroup
              value={field.state.value}
              onChange={(event) => {
                field.handleChange(event.target.value as typeof field.state.value)
                handleGoalChange(event.target.value as SetupGoal)
                form.validateField("setup.forecast", "change")
              }}
              className="setup-goal-radio-group"
            >
              <GoalRadio
                value={SETUP_GOALS.TARGET_TOTAL_RISK}
                label={getGoalLabel(totalTargetRiskConfig, hasBase)}
                fieldName="setup.targetTotalRiskValue"
                selectedValue={field.state.value}
                defaultValue={currentTotalRiskGoalValue.displayValue}
                status={totalRiskStatus}
                valueUnit={totalTargetRiskConfig.valueUnit}
                validator={targetTotalRiskSchema}
                hasBase={hasBase}
                disable={disableForecasts}
              />
              <GoalRadio
                value={SETUP_GOALS.MIN_VOLATILITY}
                label={getGoalLabel(minVolatilityConfig, hasBase)}
                selectedValue={field.state.value}
                defaultValue={currentTotalRiskGoalValue.displayValue}
                status={totalRiskStatus}
                valueUnit={minVolatilityConfig.valueUnit}
                hasBase={hasBase}
              />
              <FormControlLabel
                value={SETUP_GOALS.MAXIMIZE_SPECIFIC_RISK}
                control={<Radio size="small" />}
                label={getGoalLabel(maxSpecificRiskConfig, hasBase)}
                className="setup-goal-radio-option"
              />
            </RadioGroup>
          )}
        </form.Field>
        <form.Field
          name="setup.forecast"
          validators={{ onChange: forecastValidator(() => form.state.values.setup.goal) }}
        >
          {(field) => (
            <Box className="setup-forecast">
              <Forecast
                forecast={field.state.value}
                onChange={field.handleChange}
                forecasts={forecasts}
                forecastsStatus={forecastsStatus}
                disableForecasts={disableForecasts}
              />
              <form.Subscribe
                selector={(state) => state.fieldMeta["setup.forecast"]?.errors?.[0]}
              >
                {(errorMessage) => errorMessage ? (
                  <Typography variant="caption" className="forecast-error">
                    {errorMessage}
                  </Typography>
                ) : null}
              </form.Subscribe>
            </Box>
          )}
        </form.Field>
      </FormControl>
    </Box>
  )
}
